import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Loading, Modal, UnorderedList, ListItem } from '@carbon/react';

import { hideForm } from 'services/forms';
import { showToast } from 'services/toasts';
import { useDeleteElementMutation } from 'services/element';
import { informAboutRemovedElements } from '@/redux/new_services/element';
import { useDeleteVariantGroupMutation } from '@/redux/new_services/variantGroups';

const cascadeFromConflict = (conflict) => {
  if (!conflict) return null;
  if (conflict.cascade_required) return conflict.cascade_required;
  if ((conflict.variant_groups || []).length > 0) return 'variant_group';
  if (conflict.pair_partner) return 'pair';
  return null;
};

const cascadeForTile = (element) => {
  if (element?._pair_partner) return 'pair';
  return null;
};

const DeleteMultipleElementsModal = ({ elements, hideModal }) => {
  const dispatch = useDispatch();
  const [deleteElement] = useDeleteElementMutation();
  const [deleteVariantGroup] = useDeleteVariantGroupMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [conflicts, setConflicts] = useState(null);

  const variantGroupTiles = (elements || []).filter((el) => el?._is_variant_group);
  const pairTiles = (elements || []).filter((el) => el?._pair_partner && !el?._is_variant_group);

  const runDeletePass = async (els, force) => {
    const deleted = [];
    const remainingConflicts = [];
    let otherError = false;

    for (const element of els) {
      try {
        if (element?._is_variant_group) {
          const groupID = element._variant_group?.model?.ID;
          if (!groupID) {
            otherError = true;
            continue;
          }
          await deleteVariantGroup(groupID).unwrap();
          deleted.push(element);
          continue;
        }

        await deleteElement({
          id: element.model.ID,
          force,
          cascade: force ? cascadeForTile(element) : null,
        }).unwrap();
        deleted.push(element);
      } catch (err) {
        if (!force && err?.status === 409) {
          remainingConflicts.push({ element, conflict: err.data || {} });
        } else {
          otherError = true;
        }
      }
    }

    return { deleted, remainingConflicts, otherError };
  };

  const informDeleted = (deleted) => {
    const ids = [];
    for (const element of deleted) {
      if (element?.model?.ID) ids.push(element.model.ID);
      if (element?._pair_partner?.model?.ID) ids.push(element._pair_partner.model.ID);
    }
    if (ids.length > 0) {
      dispatch(informAboutRemovedElements(ids));
    } else if (deleted.length > 0) {
      dispatch(informAboutRemovedElements(['variant-groups']));
    }
  };

  const onInitialConfirm = async () => {
    dispatch(hideForm());
    setIsLoading(true);

    const { deleted, remainingConflicts, otherError } = await runDeletePass(elements, false);
    informDeleted(deleted);

    if (remainingConflicts.length === 0) {
      if (deleted.length > 0) {
        dispatch(showToast({
          kind: 'success',
          title: 'Elemente gelöscht',
          caption: 'Die Elemente wurden erfolgreich gelöscht',
        }));
      }
      if (otherError) {
        dispatch(showToast({
          kind: 'error',
          title: 'Fehler',
          caption: 'Beim Löschen sind Fehler aufgetreten',
        }));
      }
      setIsLoading(false);
      hideModal();
      return;
    }

    setConflicts(remainingConflicts);
    setIsLoading(false);
  };

  const onForceConfirm = async () => {
    setIsLoading(true);

    const stillPending = conflicts.map((c) => ({
      ...c.element,
      _forcedCascade: cascadeFromConflict(c.conflict),
    }));

    const deleted = [];
    let otherError = false;
    for (const element of stillPending) {
      try {
        await deleteElement({
          id: element.model.ID,
          force: true,
          cascade: element._forcedCascade || cascadeForTile(element),
        }).unwrap();
        deleted.push(element);
      } catch {
        otherError = true;
      }
    }

    informDeleted(deleted);
    if (deleted.length > 0) {
      dispatch(showToast({
        kind: 'success',
        title: 'Elemente gelöscht',
        caption: 'Die Elemente wurden gelöscht und aus den verwendeten Streams entfernt',
      }));
    }
    if (otherError) {
      dispatch(showToast({
        kind: 'error',
        title: 'Fehler',
        caption: 'Beim Löschen sind Fehler aufgetreten',
      }));
    }

    setIsLoading(false);
    hideModal();
  };

  if (conflicts) {
    return (
      <Modal
        open={true}
        danger={true}
        size="md"
        primaryButtonText="Trotzdem löschen"
        secondaryButtonText="Abbrechen"
        modalHeading="Elemente werden verwendet"
        onRequestClose={hideModal}
        onRequestSubmit={onForceConfirm}>
        <p style={{ marginBottom: '0.75rem' }}>
          Die folgenden Elemente haben weitere Verknüpfungen. Beim Löschen
          werden diese Verknüpfungen ebenfalls entfernt:
        </p>
        <UnorderedList>
          {conflicts.map(({ element, conflict }) => {
            const streams = conflict.streams || [];
            const groups = conflict.variant_groups || [];
            return (
              <ListItem key={element.model.ID}>
                <strong>{element.name || `Element #${element.model.ID}`}</strong>
                {groups.length > 0 && (
                  <UnorderedList nested>
                    {groups.map((g) => (
                      <ListItem key={g.id}>
                        Varianten-Gruppe "{g.name || `#${g.id}`}" mit allen Varianten und Medien
                      </ListItem>
                    ))}
                  </UnorderedList>
                )}
                {conflict.pair_partner && groups.length === 0 && (
                  <UnorderedList nested>
                    <ListItem>
                      Hoch+Quer-Partner "{conflict.pair_partner.name || `#${conflict.pair_partner.id}`}"
                    </ListItem>
                  </UnorderedList>
                )}
                {streams.length > 0 && (
                  <UnorderedList nested>
                    {streams.map((s) => (
                      <ListItem key={s.id}>Stream "{s.name || `#${s.id}`}"</ListItem>
                    ))}
                  </UnorderedList>
                )}
              </ListItem>
            );
          })}
        </UnorderedList>
        <Loading active={isLoading} withOverlay={true} />
      </Modal>
    );
  }

  return (
    <Modal
      open={true}
      danger={true}
      size="sm"
      primaryButtonText="Löschen"
      secondaryButtonText="Abbrechen"
      modalHeading="Sollen die ausgewählten Elemente wirklich gelöscht werden?"
      onRequestClose={hideModal}
      onRequestSubmit={onInitialConfirm}>
      {(variantGroupTiles.length > 0 || pairTiles.length > 0) && (
        <>
          <p style={{ marginBottom: '0.75rem' }}>
            Die Auswahl enthält zusammengehörende Elemente:
          </p>
          <UnorderedList>
            {variantGroupTiles.map((tile) => (
              <ListItem key={`group-${tile._variant_group?.model?.ID || tile.model.ID}`}>
                Varianten-Gruppe "{tile.name}" wird mit allen Varianten und enthaltenen Medien soft-gelöscht.
              </ListItem>
            ))}
            {pairTiles.map((tile) => (
              <ListItem key={`pair-${tile.model.ID}`}>
                Hoch+Quer-Paar "{tile.name}" wird mit beiden Formaten soft-gelöscht.
              </ListItem>
            ))}
          </UnorderedList>
        </>
      )}
      <Loading active={isLoading} withOverlay={true} />
    </Modal>
  );
};

export default DeleteMultipleElementsModal;
